(() => {
  const AUTOPLAY_DELAY = 7000;
  const DRAG_START_THRESHOLD = 4;
  const WHEEL_SETTLE_DELAY = 180;

  function initCarousel(carousel) {
    if (!carousel || carousel.dataset.hyHeroCarouselReady === 'true') return;

    const viewport = carousel.querySelector('[data-hy-hero-viewport]');
    const slides = Array.from(carousel.querySelectorAll('[data-hy-hero-slide]'));
    const indicators = Array.from(carousel.querySelectorAll('[data-hy-hero-indicator]'));

    if (!viewport || slides.length < 2 || indicators.length !== slides.length) return;

    carousel.dataset.hyHeroCarouselReady = 'true';

    let activeIndex = 0;
    let activeProgress = 0;
    let progressFrame = null;
    let cycleStartedAt = 0;
    let scrollFrame = null;
    let wheelTimer = null;

    let pointerId = null;
    let pointerType = '';
    let pointerStartX = 0;
    let pointerStartScroll = 0;
    let pointerStartIndex = 0;
    let pointerMoved = false;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function slideWidth() {
      return viewport.clientWidth || carousel.clientWidth || 1;
    }

    function clampIndex(index) {
      return Math.max(0, Math.min(slides.length - 1, index));
    }

    function renderProgress() {
      indicators.forEach((indicator, index) => {
        const isComplete = index < activeIndex;
        const isActive = index === activeIndex;
        const progress = isComplete ? 1 : isActive ? activeProgress : 0;

        indicator.classList.toggle('is-complete', isComplete);
        indicator.classList.toggle('is-active', isActive);
        indicator.setAttribute('aria-current', isActive ? 'true' : 'false');
        indicator.style.setProperty('--hy-hero-progress', String(progress));
      });
    }

    function setActive(index, restartCurrent = false) {
      const normalized = clampIndex(index);
      const changed = normalized !== activeIndex;
      activeIndex = normalized;

      if (changed || restartCurrent) {
        activeProgress = 0;
      }

      slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === normalized ? 'false' : 'true');
      });

      renderProgress();
    }

    function indexFromScroll() {
      return clampIndex(Math.round(viewport.scrollLeft / slideWidth()));
    }

    function updateFromScroll() {
      if (scrollFrame) return;

      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null;
        const nextIndex = indexFromScroll();

        if (nextIndex !== activeIndex) {
          setActive(nextIndex, true);
        }
      });
    }

    function scrollToSlide(index, behavior = 'smooth', restartCurrent = true) {
      const normalized = ((index % slides.length) + slides.length) % slides.length;
      setActive(normalized, restartCurrent);

      viewport.scrollTo({
        left: normalized * slideWidth(),
        behavior: reducedMotion.matches ? 'auto' : behavior,
      });
    }

    function stopCycle() {
      if (progressFrame) {
        window.cancelAnimationFrame(progressFrame);
        progressFrame = null;
      }
    }

    function startCycle(resetCurrent = true) {
      stopCycle();

      if (resetCurrent) {
        activeProgress = 0;
        cycleStartedAt = performance.now();
        renderProgress();
      }

      if (reducedMotion.matches || document.hidden) {
        if (reducedMotion.matches) {
          activeProgress = 1;
          renderProgress();
        }
        return;
      }

      if (!cycleStartedAt) {
        cycleStartedAt = performance.now();
      }

      const tick = (now) => {
        activeProgress = Math.min(1, (now - cycleStartedAt) / AUTOPLAY_DELAY);
        renderProgress();

        if (activeProgress >= 1) {
          const nextIndex = (activeIndex + 1) % slides.length;

          scrollToSlide(nextIndex, 'smooth', true);
          cycleStartedAt = now;
        }

        progressFrame = window.requestAnimationFrame(tick);
      };

      progressFrame = window.requestAnimationFrame(tick);
    }

    function beginInteraction() {
      stopCycle();
    }

    function endInteraction() {
      cycleStartedAt = performance.now();
      startCycle(true);
    }

    function isInteractiveTarget(target) {
      return Boolean(
        target.closest(
          'a, button, input, select, textarea, summary, [data-hy-hero-indicator]'
        )
      );
    }

    viewport.addEventListener('scroll', updateFromScroll, { passive: true });

    carousel.addEventListener('pointerdown', (event) => {
      if (isInteractiveTarget(event.target)) return;

      pointerType = event.pointerType;
      pointerStartX = event.clientX;
      pointerStartScroll = viewport.scrollLeft;
      pointerStartIndex = indexFromScroll();
      pointerMoved = false;

      beginInteraction();

      if (event.pointerType === 'touch') {
        pointerId = event.pointerId;
        return;
      }

      pointerId = event.pointerId;
      carousel.classList.add('is-dragging');
      carousel.setPointerCapture?.(pointerId);
      event.preventDefault();
    });

    carousel.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId || pointerType === 'touch') return;

      const delta = event.clientX - pointerStartX;

      if (Math.abs(delta) >= DRAG_START_THRESHOLD) {
        pointerMoved = true;
      }

      viewport.scrollLeft = pointerStartScroll - delta;

      if (pointerMoved) {
        event.preventDefault();
      }
    });

    function finishPointer(event) {
      if (pointerId !== event.pointerId) return;

      const finishingTouch = pointerType === 'touch';
      const delta = event.clientX - pointerStartX;

      if (!finishingTouch) {
        carousel.releasePointerCapture?.(pointerId);
        carousel.classList.remove('is-dragging');
      }

      pointerId = null;
      pointerType = '';

      window.requestAnimationFrame(() => {
        if (finishingTouch) {
          scrollToSlide(indexFromScroll(), 'smooth', true);
        } else {
          const changeThreshold = Math.min(72, Math.max(32, slideWidth() * 0.06));
          let nextIndex = indexFromScroll();

          if (Math.abs(delta) >= changeThreshold) {
            nextIndex = clampIndex(pointerStartIndex + (delta < 0 ? 1 : -1));
          }

          scrollToSlide(nextIndex, 'smooth', true);
        }

        endInteraction();
      });
    }

    carousel.addEventListener('pointerup', finishPointer);
    carousel.addEventListener('pointercancel', finishPointer);

    carousel.addEventListener(
      'click',
      (event) => {
        if (!pointerMoved) return;

        event.preventDefault();
        event.stopPropagation();
        pointerMoved = false;
      },
      true
    );

    carousel.addEventListener(
      'wheel',
      (event) => {
        const horizontalGesture =
          Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;

        if (!horizontalGesture) return;

        beginInteraction();
        window.clearTimeout(wheelTimer);

        wheelTimer = window.setTimeout(() => {
          scrollToSlide(indexFromScroll(), 'smooth', true);
          endInteraction();
        }, WHEEL_SETTLE_DELAY);
      },
      { passive: true }
    );

    indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        beginInteraction();
        scrollToSlide(index, 'smooth', true);
        endInteraction();
      });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopCycle();
      } else {
        startCycle(true);
      }
    });

    reducedMotion.addEventListener?.('change', () => startCycle(true));

    const resizeObserver = new ResizeObserver(() => {
      viewport.scrollTo({
        left: activeIndex * slideWidth(),
        behavior: 'auto',
      });
    });
    resizeObserver.observe(viewport);

    setActive(0, true);
    startCycle(true);
  }

  function initAll(root = document) {
    root.querySelectorAll?.('[data-hy-hero-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
